import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUrl } from '@utils/fn';
import { ScrollMenuBar as ScrollMenuBarStyled, MenuWrapper } from './styles';
import { ScrollableEventPosterSectionMenu } from '../ScrollableEventPosterSectionMenu';
import { EventPosterSectionMenu } from '../EventPosterSectionMenu';

const ALWAYS_VISIBLE_SCROLL_MENU_PATTERNS = ['content-hub-url'];

const escapeForRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const ScrollMenuBar = () => {
  const { t } = useTranslation();
  const [showScrollMenu, setShowScrollMenu] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const location = useLocation();

  const alwaysVisiblePatterns = ALWAYS_VISIBLE_SCROLL_MENU_PATTERNS.map(key => {
    const translatedPath = getUrl(t(key)).replace(/^\//, '');
    return new RegExp(
      `^/events/[^/]+/${escapeForRegex(translatedPath)}/[^/]+$`
    );
  });

  // Check if we're on the event home page (exact match: /events/:slug)
  const isEventHomePage = /^\/events\/[^/]+$/.test(location.pathname);
  const isAlwaysVisibleScrollMenuPage = alwaysVisiblePatterns.some(pattern =>
    pattern.test(location.pathname)
  );

  // Show menu after animations complete (only delay on event home page)
  useEffect(() => {
    if (isEventHomePage) {
      // Reset menu state first
      setShowMenu(false);

      // Delay for animation: logo(1s) + date(1s) + buttons(1s) + co-located(0.5s)
      const timer = setTimeout(() => {
        setShowMenu(true);
      }, 2500);

      return () => {
        clearTimeout(timer);
      };
    } else {
      // Show immediately on sub-pages
      setShowMenu(true);
    }
  }, [isEventHomePage, location.pathname]);

  // Scroll detection for menu bar (drawer behavior only on home page)
  useEffect(() => {
    if (isAlwaysVisibleScrollMenuPage) {
      setShowScrollMenu(true);
      return;
    }

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const threshold = 30; // Show menu bar after 30px scroll

      // Drawer behavior: show if scrolled 30px+, hide only when back at top
      if (scrollY >= threshold) {
        setShowScrollMenu(true);
      } else if (scrollY === 0) {
        setShowScrollMenu(false);
      }
      // If between 0 and 30px, keep current state (don't change)
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isAlwaysVisibleScrollMenuPage]);

  return (
    <MenuWrapper $shouldAnimate={showMenu}>
      {!isAlwaysVisibleScrollMenuPage && <EventPosterSectionMenu />}

      <ScrollMenuBarStyled
        $isVisible={isAlwaysVisibleScrollMenuPage ? true : showScrollMenu}
      >
        <ScrollableEventPosterSectionMenu />
      </ScrollMenuBarStyled>
    </MenuWrapper>
  );
};
